import React from 'react';
import { ColumnDef } from '@tanstack/react-table';
import TraceButton from './TraceButton';
import { VIEWED_TONE_CLASSES } from '../ProTableCell';
import { useViewedPros } from '../../contexts/ViewedProsContext';
import type { TraceCarrierType } from '../../services/tracking.service';

/**
 * The carrier cell (SSLine / SCAC, Rail / Terminal) with its per-row run-trace
 * control, plus the decorator that puts it on a column generated from a field
 * definition (the LC and Dispatch bucket tables).
 *
 * Lives here rather than in `utils/dynamicColumns` on purpose: that module is a
 * pure column-generation utility imported by plain node-environment tests, and
 * `TraceButton` reaches the API service (and through it MSAL) at module load.
 * Keeping the UI-bearing parts on the trace side leaves the generator pure.
 *
 * `proCardColumns.tsx` renders the same cell for the static PRO column set, so a
 * traced carrier looks and behaves identically whichever way its column was built.
 */

/** The carrier fields that can carry a run-trace control, and which side of the
 *  legacy trace each one forces — the same `scac` = SSLine / `rail` = Terminal
 *  split the per-field ➡️ buttons on /pro use. */
export const TRACE_CARRIER_FIELDS: Record<string, TraceCarrierType> = {
  scac: 'scac',
  rail: 'rail',
};

/** Keeps the trace pill in its own namespace per carrier side, so marking an
 *  SSLine trace never tints that row's Terminal cell (they are separate traces),
 *  and neither collides with the PRO-number pill's own tracking. */
export const traceViewedGroup = (tracetype: TraceCarrierType) => `trace-${tracetype}`;

/** Shared so a traced carrier cell looks the same whether its column came from
 *  the static PRO set or a field definition. */
export const TRACE_CELL_ICON_CLS = 'h-4 w-4';
export const TRACE_CELL_BUTTON_CLS =
  'inline-flex shrink-0 items-center justify-center h-4 w-4 rounded text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 disabled:opacity-50';

/**
 * A carrier value plus the v1 ➡️ that traces THIS pro on THIS carrier and shows
 * the outcome in the same TraceResultModal /pro opens — without opening the order.
 *
 * Once clicked, the cell wears the same green pill a clicked PRO number does. It
 * is marked on the CLICK, not on the result: a trace often comes back empty the
 * first time because it has only just opened the connection with the carrier, and
 * the user still needs to see that they have already run this one.
 *
 * The button is omitted, not disabled, when the cell is blank (no carrier means
 * nothing to trace) or the row has no numeric pronumber (a template, which /pro
 * also refuses to trace).
 */
export const TraceCarrierCell: React.FC<{
  pronumber: unknown;
  carrier: string;
  tracetype: TraceCarrierType;
  /** Called after a non-error trace so the host can re-read the row. Its absence
   *  is what turns the control off for a host that cannot refresh. */
  onTraced?: (pronumber: number) => void;
}> = ({ pronumber, carrier, tracetype, onTraced }) => {
  const { isViewed, isLastViewed, markViewed } = useViewedPros();
  const pro = Number(pronumber);
  const group = traceViewedGroup(tracetype);
  const canTrace = !!onTraced && Number.isFinite(pro) && pro > 0;

  if (!canTrace) return <span>{carrier}</span>;

  const traced = isViewed(pro, group);
  const lastTraced = isLastViewed(pro, group);
  const tone = VIEWED_TONE_CLASSES.green;

  return (
    <span
      title={lastTraced ? 'Last traced' : traced ? 'Traced' : undefined}
      className={`inline-flex items-center gap-1 min-w-0 transition-colors duration-150 ${
        traced ? tone.viewed : ''
      } ${lastTraced ? tone.lastViewed : ''}`}
    >
      <span className="truncate">{carrier}</span>
      <TraceButton
        pronumber={pro}
        variant="arrow"
        tracetype={tracetype}
        carrier={carrier}
        iconClassName={TRACE_CELL_ICON_CLS}
        className={TRACE_CELL_BUTTON_CLS}
        onTraceStart={() => markViewed(pro, group)}
        onTraced={() => onTraced!(pro)}
      />
    </span>
  );
};

/**
 * If `fieldname` is a traceable carrier field (SCAC / Rail-Terminal), return `col`
 * with its cell swapped for a TraceCarrierCell; otherwise return `col` unchanged.
 *
 * Decorates rather than replaces so the column keeps the header, accessor, sort,
 * column-filter and export behaviour its field definition gave it — the registry
 * still owns what the column is called, and everything downstream still sees the
 * plain carrier string.
 *
 * `onTraced` is what enables the control at all: a host that cannot refresh the
 * traced row should pass nothing rather than leave a stale row sitting behind a
 * fresh result.
 */
export function decorateTraceColumn(
  col: ColumnDef<any>,
  fieldname: string,
  onTraced?: (pronumber: number) => void,
): ColumnDef<any> {
  const tracetype = TRACE_CARRIER_FIELDS[fieldname];
  if (!tracetype || !onTraced) return col;
  return {
    ...col,
    cell: ({ row, getValue }) => {
      const raw = getValue();
      const carrier = raw === null || raw === undefined ? '' : String(raw).trim();
      if (!carrier) return '';
      return (
        <TraceCarrierCell
          pronumber={row.original?.pronumber}
          carrier={carrier}
          tracetype={tracetype}
          onTraced={onTraced}
        />
      );
    },
  };
}
